// This file is part of CDS RDM
// Copyright (C) 2025 CERN.
//
// CDS RDM is free software; you can redistribute it and/or modify it
// under the terms of the GPL-2.0 License; see LICENSE file for more details.

import React, { Component } from "react";
import { Button, Divider, Grid, Header, Icon, Step } from "semantic-ui-react";
import { i18next } from "@translations/invenio_app_rdm/i18next";
import { CommitteeApprovalSubmitModal } from "./CommitteeApprovalSubmitModal";
import { CreatePublicRecordModal } from "./CreatePublicRecordModal";
import PropTypes from "prop-types";

export class CommitteeApprovalManageSection extends Component {
  constructor(props) {
    super(props);
    const recordManagementDiv = document.getElementById("recordManagement");
    const committeeApprovalData = recordManagementDiv
      ? JSON.parse(recordManagementDiv.dataset.committeeApproval || "null")
      : null;

    this.state = {
      committeeApproval: committeeApprovalData,
      submitModalOpen: false,
      createPublicModalOpen: false,
      // Tracks the public record id/url after creation (optimistic UI update).
      // can_create_public from backend already handles the case where one exists.
      publicRecordId: null,
      publicRecordUrl: null,
    };

    this._newVersionClickHandler = null;
  }

  get shouldRender() {
    const { committeeApproval } = this.state;
    return (
      committeeApproval &&
      (committeeApproval.community_enrolled ||
        committeeApproval.is_public_approved_record)
    );
  }

  handlePublicRecordCreated = (publicRecord) => {
    this.setState({
      publicRecordId: publicRecord.id,
      publicRecordUrl: publicRecord.links?.self_html || null,
    });
  };

  render() {
    const {
      committeeApproval,
      submitModalOpen,
      createPublicModalOpen,
      publicRecordId,
      publicRecordUrl,
    } = this.state;
    const { record } = this.props;

    if (!this.shouldRender) {
      return null;
    }

    // Public EP-approved record — show a compact provenance note.
    if (committeeApproval.is_public_approved_record) {
      const {
        approval_label: approvalLabel,
        approved_report_number: pubRn,
        draft_record_id: draftRecordId,
        can_view_reviewed_version: canViewReviewedVersion,
      } = committeeApproval;

      return (
        <Grid.Column className="pb-20 pt-0">
          <Divider horizontal>
            <Header as="h4">
              {i18next.t("{{approvalLabel}} request workflow", {
                approvalLabel,
              })}
            </Header>
          </Divider>

          <p className="text-muted text-align-center">
            {pubRn?.length
              ? i18next.t("EP-approved as ")
              : i18next.t("EP-approved record")}

            {pubRn?.length > 0 && <strong>{pubRn.join(", ")}</strong>}

            {canViewReviewedVersion && draftRecordId && (
              <>
                {" · "}
                <a href={`/records/${draftRecordId}`} target="_blank" rel="noreferrer">
                  {i18next.t("Original record")}
                  <Icon name="external alternate" className="ml-5" />
                </a>
              </>
            )}
          </p>
        </Grid.Column>
      );
    }

    const {
      can_submit: canSubmit,
      can_create_public: canCreatePublicFlag,
      approval_label: approvalLabel,
      committee_name: committeeName,
      open_request: openRequest,
      approved_report_number: approvedReportNumber,
      receiver_group: receiverGroup,
      committee_approval: committeeApprovalField,
    } = committeeApproval;

    // Public record URL: prefer the URL captured at creation time; fall back to
    // the recid stored on the parent (approved_public_version) for page-load case.
    const resolvedPublicRecordUrl =
      publicRecordUrl ||
      (committeeApprovalField?.approved_public_version
        ? `/records/${committeeApprovalField.approved_public_version}`
        : null);

    const isPending = openRequest?.status === "submitted";
    const isDeclined = openRequest?.status === "declined";
    const isAccepted = openRequest?.status === "accepted";
    // approvedReportNumber is always populated by the backend (scans the full
    // parent if the current version doesn't carry the CF itself).
    const canResubmit =
      canSubmit && !isPending && !approvedReportNumber?.length && !isAccepted;
    // canCreatePublicFlag comes from the backend and already encodes version-order
    // eligibility (only versions >= the approved version may create a public record).
    const canCreatePublic = canCreatePublicFlag && !publicRecordId;

    const requestLink =
      openRequest?.links?.self_html ||
      (openRequest?.id ? `/requests/${openRequest.id}` : null);

    // Timeline step states
    // Step 1 — Request for approval
    const step1Completed = !!openRequest && !isDeclined;
    const step1Active = !step1Completed && !isPending;

    // Step 2 — EP Board review
    const step2Completed = !!approvedReportNumber?.length;
    const step2Active = isPending;
    const step2Disabled = !isPending && !step2Completed;

    // Step 3 — Create final public version
    const step3Completed = step2Completed && !canCreatePublic;
    const step3Active = step2Completed && canCreatePublic;
    const step3Disabled = !step2Completed;

    return (
      <Grid.Column className="pb-20 pt-0">
        <Divider horizontal>
          <Header as="h4">
            {i18next.t("{{approvalLabel}} request workflow", {
              approvalLabel,
            })}
          </Header>
        </Divider>

        <Step.Group ordered vertical fluid size="mini" className="committee-step-group">
          {/* Step 1 — Request for approval */}
          <Step completed={step1Completed} active={step1Active}>
            <Step.Content>
              <div className="committee-action-step">
                <div>
                  <Step.Title>{i18next.t("Request for approval")}</Step.Title>
                  <Step.Description>
                    {isDeclined ? (
                      <>
                        {i18next.t("Request was declined.")}
                        {requestLink && (
                          <>
                            {" "}
                            <a href={requestLink} target="_blank" rel="noreferrer">
                              {i18next.t("View")}
                              <Icon name="external alternate" className="ml-5" />
                            </a>
                          </>
                        )}
                      </>
                    ) : step1Completed ? (
                      i18next.t("Request submitted.")
                    ) : (
                      i18next.t("Submit the document for {{committeeName}} review.", {
                        committeeName,
                      })
                    )}
                  </Step.Description>
                </div>
                {canResubmit && (
                  <Button
                    positive
                    size="mini"
                    onClick={() => this.setState({ submitModalOpen: true })}
                  >
                    {isDeclined
                      ? i18next.t("Request again")
                      : i18next.t("Request approval")}
                  </Button>
                )}
              </div>
            </Step.Content>

            {canResubmit && (
              <CommitteeApprovalSubmitModal
                open={submitModalOpen}
                record={record}
                receiverGroup={receiverGroup}
                onClose={() => this.setState({ submitModalOpen: false })}
              />
            )}
          </Step>

          {/* Step 2 — EP Board review */}
          <Step
            completed={step2Completed}
            active={step2Active}
            disabled={step2Disabled}
          >
            <Step.Content>
              <div className="committee-action-step">
                <div>
                  <Step.Title>
                    {i18next.t("{{committeeName}} review", {
                      committeeName,
                    })}
                  </Step.Title>
                  <Step.Description>
                    {step2Completed ? (
                      requestLink ? (
                        <a href={requestLink} target="_blank" rel="noreferrer">
                          {i18next.t("Approved as {{rn}}", {
                            rn: approvedReportNumber.join(", "),
                          })}
                          <Icon name="external alternate" className="ml-5" />
                        </a>
                      ) : (
                        i18next.t("Approved as {{rn}}", {
                          rn: approvedReportNumber.join(", "),
                        })
                      )
                    ) : (
                      i18next.t("The {{committeeName}} will review the submission.", {
                        committeeName,
                      })
                    )}
                  </Step.Description>
                </div>
                {step2Active && requestLink && (
                  <Button
                    as="a"
                    size="mini"
                    href={requestLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {i18next.t("View request")}
                    <Icon name="external alternate" className="ml-5" />
                  </Button>
                )}
              </div>
            </Step.Content>
          </Step>

          {/* Step 3 — Create final public version */}
          <Step
            completed={step3Completed}
            active={step3Active}
            disabled={step3Disabled}
          >
            <Step.Content>
              <div className="committee-action-step">
                <div>
                  <Step.Title>
                    {step3Completed
                      ? i18next.t("Final public version created")
                      : i18next.t("Create final public version")}
                  </Step.Title>
                  <Step.Description>
                    {step3Completed ? (
                      resolvedPublicRecordUrl ? (
                        <a
                          href={resolvedPublicRecordUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {i18next.t("View public record")}
                          <Icon name="external alternate" className="ml-5" />
                        </a>
                      ) : (
                        i18next.t("Public record created.")
                      )
                    ) : (
                      i18next.t("Publish the approved record publicly on CDS.")
                    )}
                  </Step.Description>
                </div>
                {step3Active && (
                  <Button
                    primary
                    size="mini"
                    onClick={() => this.setState({ createPublicModalOpen: true })}
                  >
                    {i18next.t("Publish")}
                  </Button>
                )}
              </div>
            </Step.Content>
            {step3Active && (
              <CreatePublicRecordModal
                open={createPublicModalOpen}
                record={record}
                approvedReportNumber={approvedReportNumber}
                onClose={() => this.setState({ createPublicModalOpen: false })}
                onSuccess={this.handlePublicRecordCreated}
              />
            )}
          </Step>
        </Step.Group>
      </Grid.Column>
    );
  }
}

CommitteeApprovalManageSection.propTypes = {
  record: PropTypes.object.isRequired,
};
